import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { dayjs, CommonDocumentService } from '@umanforge/common';

import { DriveItem } from './drive_item.types';
import { FolderIcon } from './folder_icon.component';
import { FileTypeIcon } from './file_type_icon.component';
import { DotsIcon } from '../icons/dots.icon';
import { TrashIcon } from '../icons/trash.icon';


interface DriveListViewProps {
  items: DriveItem[];
  onDeleteSelected?: ( ids: string[] ) => Promise<void> | void;
}

function formatSize( item: DriveItem ): string {
  if ( item.type === 'folder' ) {
    return '—';
  }
  const kilo = item.size;
  if ( kilo == null || kilo < 0 ) { return '—'; }
  return CommonDocumentService.getReadableFileSizeString( kilo );
}

function formatCount( item: DriveItem, t: ( k: string ) => string ): string {
  if ( item.type === 'file' ) { return '—'; }
  const n = item.size;
  if ( n === 0 ) { return t( 'translation:0_items' ); }
  if ( n === 1 ) { return t( 'translation:1_item' ); }
  return `${ n } ${ t( 'translation:items' ) }`;
}

function formatDate( date: Date | string | undefined ): string {
  if ( !date ) { return '—'; }
  const d = dayjs( date );
  if ( !d.isValid() ) { return '—'; }
  return d.format( 'DD/MM/YYYY HH:mm' );
}

function RowMenu( { item }: { item: DriveItem } ) {
  const [ open, setOpen ] = useState( false );
  const wrapperRef = useRef<HTMLDivElement>( null );
  const buttonRef = useRef<HTMLButtonElement>( null );
  const portalRef = useRef<HTMLDivElement>( null );
  const [ coords, setCoords ] = useState<{ top: number; left: number } | null>( null );
  const [ buttonRect, setButtonRect ] = useState<DOMRect | null>( null );

  useEffect( () => {
    if ( !open ) { return; }
    const rect = buttonRef.current?.getBoundingClientRect();
    if ( rect ) {
      setButtonRect( rect );
      const menuMinWidth = 180; // match min-w-[180px]
      const padding = 12; // small offset from edge
      const initialLeft = rect.right - menuMinWidth;
      const left = Math.max( padding, Math.min( initialLeft, window.innerWidth - menuMinWidth - padding ) );
      setCoords( { top: rect.bottom + 8, left } );
    }

    const handler = ( e: MouseEvent ) => {
      const target = e.target as Node;
      if ( wrapperRef.current && wrapperRef.current.contains( target ) ) { return; }
      if ( portalRef.current && portalRef.current.contains( target ) ) { return; }
      setOpen( false );
    };
    document.addEventListener( 'mousedown', handler );
    return () => { document.removeEventListener( 'mousedown', handler ); };
  }, [ open ] );

  useEffect( () => {
    if ( !open ) { return; }
    const el = portalRef.current;
    if ( !el || !buttonRect ) { return; }
    const menuRect = el.getBoundingClientRect();
    const padding = 12;

    // Prefer aligning the menu's right edge with the button's right edge
    const preferredLeft = buttonRect.right - menuRect.width;
    const newLeft = Math.max( padding, Math.min( preferredLeft, window.innerWidth - menuRect.width - padding ) );

    // Position below by default, move above if doesn't fit
    const belowTop = buttonRect.bottom + 8;
    let newTop = belowTop;
    if ( belowTop + menuRect.height > window.innerHeight - padding ) {
      const aboveTop = buttonRect.top - menuRect.height - 8;
      newTop = Math.max( padding, aboveTop );
    }

    if ( !coords || Math.abs( coords.left - newLeft ) > 1 || Math.abs( coords.top - newTop ) > 1 ) {
      setCoords( { top: newTop, left: newLeft } );
    }
  }, [ open, buttonRect, coords?.left, coords?.top ] );

  useEffect( () => {
    if ( !open ) { return; }
    let rafId = 0 as number | undefined;
    const handler = () => {
      if ( rafId ) { cancelAnimationFrame( rafId ); }
      rafId = requestAnimationFrame( () => {
        const rect = buttonRef.current?.getBoundingClientRect();
        const el = portalRef.current;
        if ( !rect ) { return; }
        setButtonRect( rect );
        const padding = 12;
        if ( !el ) {
          const menuMinWidth = 180;
          const initialLeft = rect.right - menuMinWidth;
          const left = Math.max( padding, Math.min( initialLeft, window.innerWidth - menuMinWidth - padding ) );
          setCoords( { top: rect.bottom + 8, left } );
          return;
        }
        const menuRect = el.getBoundingClientRect();
        const preferredLeft = rect.right - menuRect.width;
        const newLeft = Math.max( padding, Math.min( preferredLeft, window.innerWidth - menuRect.width - padding ) );
        let newTop = rect.bottom + 8;
        if ( newTop + menuRect.height > window.innerHeight - padding ) {
          const aboveTop = rect.top - menuRect.height - 8;
          newTop = Math.max( padding, aboveTop );
        }
        setCoords( { top: newTop, left: newLeft } );
      } );
    };
    document.addEventListener( 'scroll', handler, true );
    window.addEventListener( 'resize', handler );
    return () => {
      document.removeEventListener( 'scroll', handler, true );
      window.removeEventListener( 'resize', handler );
      if ( rafId ) { cancelAnimationFrame( rafId ); }
    };
  }, [ open ] );

  if ( !item.actions || item.actions.length === 0 ) { return null; }

  return (
    <div ref={ wrapperRef } className="relative flex justify-center">
      <button
        ref={ buttonRef }
        type="button"
        className="flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
        onClick={ e => { e.stopPropagation(); setOpen( prev => { return !prev; } ); } }
      >
        <DotsIcon className="w-3 h-5 fill-current" />
      </button>
      { open && coords && createPortal(
        <div
          ref={ portalRef }
          style={ { position: 'fixed', top: `${ coords.top }px`, left: `${ coords.left }px`, zIndex: 9999 } }
        >
          <div className="flex flex-col divide-y mx-3 z-50 min-w-[180px] bg-white rounded-xl shadow-2xl border border-gray-100 justify-center">
            { item.actions.map( ( action, idx ) => {
              const key = `${ idx }-${ item.id }`;
              return (
                <button
                  key={ key }
                  type="button"
                  className={ `flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm hover:bg-gray-50 transition-colors ${ action.danger ? 'text-red-500' : 'text-gray-700' }` }
                  onClick={ e => { e.stopPropagation(); action.onClick(); setOpen( false ); } }
                >
                  { action.icon && (
                    <span className={ `flex items-center justify-center w-5 h-5 shrink-0 ${ action.danger ? 'text-red-500' : 'text-gray-500' }` }>
                      { action.icon }
                    </span>
                  ) }
                  <span>{ action.label }</span>
                </button>
              );
            } ) }
          </div>
        </div>,
        document.body,
      ) }
    </div>
  );
}

export function DriveListView( { items, onDeleteSelected }: DriveListViewProps ) {
  const { t } = useTranslation();
  const [ selected, setSelected ] = useState<Set<string>>( new Set() );

  const allSelected = items.length > 0 && selected.size === items.length;

  const toggleAll = () => {
    setSelected( allSelected ? new Set() : new Set( items.map( i => {return i.id;} ) ) );
  };

  const toggleOne = ( id: string ) => {
    setSelected( prev => {
      const next = new Set( prev );
      if ( next.has( id ) ) { next.delete( id ); } else { next.add( id ); }
      return next;
    } );
  };

  return (
    <div className="w-full rounded-xl border border-gray-100 bg-white shadow-sm relative">
      <div className={ `absolute bottom-full left-0 mb-1 z-10 flex items-center gap-3 px-4 overflow-hidden transition-all duration-200 rounded-xl shadow-md ${ selected.size > 0 ? 'h-11 py-2 bg-red-50 border border-red-200' : 'h-0 py-0 border-transparent' }` }>
        <span className="text-sm text-gray-600 whitespace-nowrap">
          { selected.size } { selected.size === 1 ? t( 'translation:selected_item' ) : t( 'translation:selected_items' ) }
        </span>
        <button
          type="button"
          className="flex items-center gap-2 px-3 py-1 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors whitespace-nowrap"
          onClick={ async () => {
            const ids = Array.from( selected );
            if ( ids.length === 0 ) { return; }
            try {
              await ( onDeleteSelected ? onDeleteSelected( ids ) : Promise.resolve() );
            } finally {
              setSelected( new Set() );
            }
          } }
        >
          <TrashIcon className="w-3.5 h-3.5 fill-white shrink-0" />
          { t( 'translation:delete_selection' ) }
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-gray-500 font-medium">
              <th className="w-10 px-4 py-3">
                <input
                  type="checkbox"
                  checked={ allSelected }
                  onChange={ toggleAll }
                  className="accent-red-500 w-4 h-4 cursor-pointer"
                />
              </th>
              <th className="w-10 px-2 py-3" />
              <th className="text-left px-3 py-3">{ t( 'translation:name' ) }</th>
              <th className="text-left px-3 py-3">{ t( 'translation:items' ) }</th>
              <th className="text-left px-3 py-3">{ t( 'translation:size' ) }</th>
              <th className="text-left px-3 py-3">{ t( 'translation:last_modified' ) }</th>
              <th className="w-16 px-3 py-3 text-center">{ t( 'translation:actions' ) }</th>
            </tr>
          </thead>
          <tbody>
            { items.map( item => {return (
              <tr
                key={ item.id }
                className={ `border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors ${ item.onClick ? 'cursor-pointer' : '' }` }
                onClick={ item.onClick ? item.onClick : undefined }
              >
                <td className="px-4 py-3" onClick={ e => { e.stopPropagation(); } }>
                  <input
                    type="checkbox"
                    checked={ selected.has( item.id ) }
                    onChange={ () => { toggleOne( item.id ); } }
                    className="accent-red-500 w-4 h-4 cursor-pointer"
                  />
                </td>
                <td className="px-2 py-3">
                  { item.type === 'folder'
                    ? <FolderIcon isEmpty={ item.size === 0 } className={ `w-8 h-8 ${ item.size === 0 ? 'text-orange-400' : 'text-red-500' }` } />
                    : <FileTypeIcon extension={ item.extension ?? '' } className="w-7 h-8" />
                  }
                </td>
                <td className="px-3 py-3 font-medium text-gray-800">
                  { item.name + ( item.extension ? item.extension : '' ) }
                </td>
                <td className="px-3 py-3 text-gray-500">
                  { formatCount( item, t ) }
                </td>
                <td className="px-3 py-3 text-gray-500">
                  { formatSize( item ) }
                </td>
                <td className="px-3 py-3 text-gray-400">
                  { formatDate( item.lastModified ) }
                </td>
                <td className="px-3 py-3" onClick={ e => { e.stopPropagation(); } }>
                  <RowMenu item={ item } />
                </td>
              </tr>
            );} ) }
          </tbody>
        </table>
      </div>
    </div>
  );
}
